import React, { createContext, useContext, useState, useCallback } from 'react';
import { Dialog, DialogOptions, DialogVariant } from '../components/ui/Dialog';
import { useTranslation } from 'react-i18next';

interface DialogContextValue {
  showDialog: (options: DialogOptions) => void;
  showAlert: (title: string, message: string, variant?: DialogVariant) => Promise<void>;
  showConfirm: (title: string, message: string, variant?: DialogVariant) => Promise<boolean>;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export const useDialogContext = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialogContext must be used within a DialogProvider');
  }
  return context;
};

interface DialogState {
  isOpen: boolean;
  options: DialogOptions;
  resolve?: (value: any) => void;
}

const defaultOptions: DialogOptions = {
  title: '',
  message: '',
  variant: 'info',
  closeOnOverlayClick: true,
  closeOnEscape: true,
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    options: defaultOptions,
  });

  const closeDialog = useCallback(() => {
    setDialogState((prev) => ({
      ...prev,
      isOpen: false,
    }));
    // Clear the dialog after animation
    setTimeout(() => {
      setDialogState((current) =>
        current.isOpen
          ? current
          : {
              isOpen: false,
              options: defaultOptions,
            }
      );
    }, 200);
  }, []);

  const showDialog = useCallback((options: DialogOptions) => {
    setDialogState({
      isOpen: true,
      options: {
        ...defaultOptions,
        ...options,
      },
    });
  }, []);

  const showAlert = useCallback(
    (title: string, message: string, variant: DialogVariant = 'info'): Promise<void> => {
      return new Promise((resolve) => {
        setDialogState({
          isOpen: true,
          options: {
            title,
            message,
            variant,
            buttons: [
              {
                label: t('actions.ok'),
                variant: variant === 'error' || variant === 'danger' ? 'danger' : 'primary',
                onClick: () => {
                  closeDialog();
                  resolve();
                },
                autoFocus: true,
              },
            ],
            onClose: () => {
              closeDialog();
              resolve();
            },
          },
        });
      });
    },
    [closeDialog, t]
  );

  const showConfirm = useCallback(
    (title: string, message: string, variant: DialogVariant = 'confirm'): Promise<boolean> => {
      return new Promise((resolve) => {
        const handleConfirm = () => {
          closeDialog();
          resolve(true);
        };

        const handleCancel = () => {
          closeDialog();
          resolve(false);
        };

        setDialogState({
          isOpen: true,
          options: {
            title,
            message,
            variant,
            buttons: [
              {
                label: t('actions.cancel'),
                variant: 'secondary',
                onClick: handleCancel,
                autoFocus: true,
              },
              {
                label: t('actions.confirm'),
                variant: variant === 'error' ? 'danger' : 'primary',
                onClick: handleConfirm,
              },
            ],
            onClose: handleCancel,
            closeOnOverlayClick: true,
            closeOnEscape: true,
          },
        });
      });
    },
    [closeDialog, t]
  );

  return (
    <DialogContext.Provider
      value={{
        showDialog,
        showAlert,
        showConfirm,
        closeDialog,
      }}
    >
      {children}
      <Dialog
        isOpen={dialogState.isOpen}
        {...dialogState.options}
        onClose={dialogState.options.onClose}
      />
    </DialogContext.Provider>
  );
};

export default DialogProvider;
